/**
 * The filter every analytics endpoint takes: who and what to count, over which
 * dates, in whose timezone. Every read in `/queries` starts from this shape.
 */
export type AnalyticsQueryFilter = {
    companyId?: string;
    teamIds?: string[];
    userIds?: string[];
    // When set, the passed userIds are companyUserIds.
    useNewUserSystem?: boolean;
    deviceIds?: string[];
    from?: string;
    to?: string;
    tz?: string;
};

export type BuildAnalyticsQueryFilterProps = {
    companyId: string;
    teamIds: Set<string>;
    userIds: Set<string>;
    deviceIds: Set<string>;
    timezone: string;
    /** Whole-day ISO bounds; the caller decides how the range is clamped. */
    from?: string;
    to?: string;
};

/**
 * Turns the reports page's filter state into the query the analytics endpoints
 * expect.
 *
 * An empty selection is left off the query entirely — sending `teamIds: []`
 * would ask the server for no teams rather than for all of them.
 */
export function buildAnalyticsQueryFilter(
    props: BuildAnalyticsQueryFilterProps
): AnalyticsQueryFilter {
    const { companyId, teamIds, userIds, deviceIds, timezone, from, to } =
        props;

    const filter: AnalyticsQueryFilter = {
        companyId,
        tz: timezone
    };

    if (teamIds.size > 0) {
        filter.teamIds = Array.from(teamIds);
    }
    if (userIds.size > 0) {
        filter.userIds = Array.from(userIds);
        filter.useNewUserSystem = true;
    }
    if (deviceIds.size > 0) {
        filter.deviceIds = Array.from(deviceIds);
    }
    if (from) {
        filter.from = from;
    }
    if (to) {
        filter.to = to;
    }

    return filter;
}
